import React from 'react';
import PropTypes from 'prop-types';
import { useFormState } from 'react-final-form';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Modal, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import Button, { BUTTON_TYPE } from '../../../components/Button';
import { BLACK, PRIMARY_COLOR } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import { epochToDateInReadableFormat, minutesFromMidnightToReadableTime } from '../../../utils';

const titleStyling = {
  color: PRIMARY_COLOR[70],
  fontFamily: PRIMARY_FONT[700],
  fontSize: '24px',
  mb: 3,
};

const headerStyling = {
  fontFamily: PRIMARY_FONT[700],
  fontSize: '18px',
  color: BLACK[100],
  mb: 2,
};

const subHeaderStyling = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '12px',
  color: BLACK[60],
  textTransform: 'uppercase',
  mb: 1,
};
const textStyling = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '14px',
  color: BLACK[100],
  mb: 1,
};

const ConfirmModal = ({ isOpen, onClose, onSubmit, isProcessing }) => {
  const smallScreen = useMediaQuery('(max-width:1100px)');
  const formState = useFormState();
  const currentFormValues = formState?.values;

  const confirmationData = {
    position: currentFormValues?.positionTemplate?.name,
    location: currentFormValues?.location?.address,
    department: currentFormValues?.department?.description,
    startTime: minutesFromMidnightToReadableTime(currentFormValues?.startTime?.value || 0),
    endTime: minutesFromMidnightToReadableTime(currentFormValues?.endTime?.value || 0),
    datesOpenings: currentFormValues?.datesOpenings || [],
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          position: 'relative',
          width: smallScreen ? '90vw' : '600px',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            maxHeight: 'calc(90vh - 32px)',
            overflowY: 'auto',
          }}
        >
          <Typography sx={titleStyling}>
            {LanguageConverterV2('job.create.confirmation.header')}
          </Typography>

          {/* Position Details Section */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={headerStyling}>
              {LanguageConverterV2('job.create.confirmation.positionDetails')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography sx={subHeaderStyling}>
                  {LanguageConverterV2('job.create.confirmation.position')}
                </Typography>
                <Typography sx={textStyling}>{confirmationData?.position}</Typography>
              </Box>
              <Box>
                <Typography sx={subHeaderStyling}>
                  {LanguageConverterV2('job.create.confirmation.location')}
                </Typography>
                <Typography sx={textStyling}>{confirmationData?.location}</Typography>
              </Box>
              <Box>
                <Typography sx={subHeaderStyling}>
                  {LanguageConverterV2('job.create.confirmation.department')}
                </Typography>
                <Typography sx={textStyling}>{confirmationData?.department}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Times Section */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={headerStyling}>
              {LanguageConverterV2('job.create.confirmation.times')}
            </Typography>
            <Box>
              <Typography sx={subHeaderStyling}>
                {LanguageConverterV2('job.create.confirmation.startAndEndTime')}
              </Typography>
              <Typography sx={textStyling}>
                {confirmationData?.startTime} - {confirmationData?.endTime}
              </Typography>
            </Box>
          </Box>

          {/* Dates and Openings Section */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={headerStyling}>
              {LanguageConverterV2('job.create.datePickerOpenings.dateAndOpenings')}
            </Typography>
            <Box
              sx={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
              }}
            >
              {confirmationData?.datesOpenings?.length > 0 ? (
                confirmationData.datesOpenings.map((item) => (
                  <Typography key={item.date} sx={textStyling}>
                    {epochToDateInReadableFormat(item.date)}: {item.openings}
                  </Typography>
                ))
              ) : (
                <Typography sx={textStyling} color="text.secondary">
                  No dates selected
                </Typography>
              )}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 4,
            }}
          >
            <Button
              text={LanguageConverterV2('job.create.edit')}
              onClick={onClose}
              sx={{
                color: '#000000',
                backgroundColor: '#ffffff',
                border: '1px solid #cccccc',
                borderRadius: '4px',
                padding: '12px 24px',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
            <Button
              data-testid="modalSubmitButton"
              type={BUTTON_TYPE.SUBMIT}
              text={LanguageConverterV2('job.create.post')}
              disabled={isProcessing}
              onClick={onSubmit}
              sx={{
                backgroundColor: PRIMARY_COLOR[70],
                color: '#ffffff',
                borderRadius: '4px',
                padding: '12px 24px',
                '&:hover': {
                  backgroundColor: PRIMARY_COLOR[80],
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

ConfirmModal.propTypes = {
  isOpen: PropTypes.bool,
  isProcessing: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default ConfirmModal;
